import { useEffect } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  OG_IMAGE_ALT,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_PATH,
  OG_IMAGE_WIDTH,
  SITE_URL,
  absoluteUrl,
} from '../constants/seo';

export interface SeoOptions {
  title?: string;
  description?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  noIndex?: boolean;
}

function upsertMeta(
  selector: string,
  attrs: Record<string, string>,
  content: string,
): void {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([key, value]) => el!.setAttribute(key, value));
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo(options: SeoOptions = {}): void {
  const title = options.title ?? DEFAULT_TITLE;
  const description = options.description ?? DEFAULT_DESCRIPTION;
  const image = absoluteUrl(options.image ?? OG_IMAGE_PATH);
  const imageWidth = String(options.imageWidth ?? OG_IMAGE_WIDTH);
  const imageHeight = String(options.imageHeight ?? OG_IMAGE_HEIGHT);
  const imageAlt = options.imageAlt ?? OG_IMAGE_ALT;
  const url = options.url ?? `${SITE_URL}${window.location.pathname}`;
  const type = options.type ?? 'website';
  const robots = options.noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';

  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, robots);

    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, url);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, type);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, image);
    upsertMeta('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url' }, image);
    upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width' }, imageWidth);
    upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height' }, imageHeight);
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, imageAlt);

    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image);
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, imageAlt);

    upsertLink('canonical', url);
  }, [
    title,
    description,
    image,
    imageWidth,
    imageHeight,
    imageAlt,
    url,
    type,
    robots,
  ]);
}
